import { useCallback, useEffect, useState } from 'react';
import { Play, Video } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import './D28dRecordingsPreview.css';

export default function D28dRecordingsPreview({ onOpenAll = null, limit = 6 }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/live-classes/class-gallery');
      setItems((res.data?.data || []).filter((item) => item.video_url));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="card d28d-rec-preview p-4 animate-pulse">
        <div className="h-4 bg-white/10 rounded w-1/3 mb-3" />
        <div className="h-8 bg-white/10 rounded w-full" />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="card d28d-rec-preview p-4 space-y-2">
        <h3 className="d28d-rec-preview__title">
          <Video className="w-4 h-4 d28d-rec-preview__title-icon" aria-hidden />
          {t('d28d.hub.recordings', 'GRABACIONES DE CLASE')}
        </h3>
        <p className="d28d-rec-preview__hint">
          {t('d28d.recordings.empty', 'Aún no hay grabaciones publicadas para tu programa.')}
        </p>
        {onOpenAll ? (
          <button type="button" className="d28d-rec-preview__see-all" onClick={onOpenAll}>
            {t('d28d.recordings.open_gallery', 'Abrir grabaciones')}
          </button>
        ) : null}
      </div>
    );
  }

  const visible = items.slice(0, limit);

  const openRecording = async (item) => {
    if (!item?.id || !item?.video_url) return;
    try {
      await api.post(`/live-classes/class-gallery/${item.id}/view`);
    } catch {
      /* no bloquear apertura */
    }
    window.open(item.video_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="card d28d-rec-preview p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="d28d-rec-preview__title">
          <Video className="w-4 h-4 d28d-rec-preview__title-icon" aria-hidden />
          {t('d28d.hub.recordings', 'GRABACIONES DE CLASE')}
        </h3>
        {onOpenAll ? (
          <button type="button" className="d28d-rec-preview__see-all shrink-0" onClick={onOpenAll}>
            {t('d28d.recordings.see_all', 'Ver todas')}
          </button>
        ) : null}
      </div>
      <p className="d28d-rec-preview__hint">
        {t('d28d.recordings.home_hint', 'Toca una clase para abrir la grabación.')}
      </p>
      <div className="d28d-rec-preview__chips">
        {visible.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => openRecording(item)}
            className="d28d-rec-chip"
            title={item.title || t('d28d.recordings.untitled', 'Sin título')}
          >
            <span className="d28d-rec-chip__icon" aria-hidden>
              <Play className="w-3.5 h-3.5" fill="currentColor" />
            </span>
            <span className="d28d-rec-chip__label">
              {item.title || t('d28d.recordings.untitled', 'Sin título')}
            </span>
            {item.view_count != null ? (
              <span className="d28d-rec-chip__meta">· {Number(item.view_count) || 0}</span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}
